import { useState } from 'react';
import { useUser } from '@/context/UserContext.js';
import { RarityKey, VoxelTypeKey, RarityInventory } from "@/types";
import { doc } from 'firebase/firestore';
import '../styles/InventoryPanel.css'; // Import the CSS file

const rarityOrder: RarityKey[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

const rarityHierarchy: { [key in RarityKey]?: RarityKey } = {
    "common": "uncommon",
    "uncommon": "rare",
    "rare": "epic",
    "epic": "legendary"
};

const VOXEL_COLORS: Record<VoxelTypeKey, string> = {
    grass: '#ffd1cc',
    dirt: '#ffffcc',
    stone: '#cceeff',
    coal_ore: '#ccffcc',
    iron_ore: '#e6ccff',
    tree_top: '#ffcccc',
    leave: '#ffe6cc',
    sand: '#f2f2f2',
    cloud: '#f0f0f0',
    snow: '#ccffff',
    jungle_tree: '#ffffe6',
    jungle_leaves: '#ccccff',
    cactus: '#ffebcc',
    jungle_grass: '#e6ffff',
    lava: '#fce6ff',
    frost_mint: '#e6ffe6'
};

const getNextRarity = (currentRarity: RarityKey): RarityKey | undefined => {
    const currentIndex = rarityOrder.indexOf(currentRarity);
    if (currentIndex === -1 || currentIndex >= rarityOrder.length - 1) {
        return undefined;
    }
    return rarityOrder[currentIndex + 1];
};


import { httpsCallable } from 'firebase/functions';
import { functions } from '@/config/firebase';

const mergeVoxelsCallable = httpsCallable(functions, 'mergeVoxels');

const mergeVoxels = async (userId: string, voxelType: VoxelTypeKey, fromRarity: RarityKey) => {
    try {
        const result = await mergeVoxelsCallable({ voxelType, fromRarity });
        console.log(`Merged 2 x ${fromRarity} ${voxelType} for user ${userId}`);
    } catch (error) {
        console.error(`Error merging voxels for user ${userId}:`, error);
        throw error;
    }
};


interface InventoryPanelProps {
    onClose: () => void;
}


const InventoryPanel = ({ onClose }: InventoryPanelProps) => {
    const { user, voxelInventory, loadingInventory } = useUser();

    const [isOpen, setIsOpen] = useState(true);
    const [isMerging, setIsMerging] = useState(false);
    // State to manage the active tab ('available' or 'used')
    const [activeTab, setActiveTab] = useState<'available' | 'used'>('available');

    const [selectedMergeCandidate, setSelectedMergeCandidate] = useState<{ rarity: RarityKey, type: VoxelTypeKey } | null>(null);

    const handleVoxelBoxClick = (rarity: RarityKey, voxelType: VoxelTypeKey, section: 'available' | 'used') => {
        if (section !== 'available') {
            setSelectedMergeCandidate(null);
            return;
        }

        const count = voxelInventory?.available?.[rarity]?.[voxelType] || 0;
        const isMergeable = count >= 2 && rarity !== 'legendary';

        if (isMergeable) {
            const isSelected = selectedMergeCandidate?.rarity === rarity && selectedMergeCandidate?.type === voxelType;
            if (isSelected) {
                setSelectedMergeCandidate(null);
            } else {
                setSelectedMergeCandidate({ rarity, type: voxelType });
            }
        } else {
            setSelectedMergeCandidate(null);
        }
    };


    const handleMergeVoxelType = async () => {
        if (!selectedMergeCandidate) {
            console.warn("No merge candidate selected.");
            return;
        }

        const { rarity, type: voxelType } = selectedMergeCandidate;
        const currentCount = voxelInventory?.available?.[rarity]?.[voxelType] || 0;
        const userPoints = user?.points || 0;


        if (!user || isMerging || rarity === 'legendary' || currentCount < 2 || userPoints < 1) {
            console.warn("Merge conditions not met before executing merge.");
            setSelectedMergeCandidate(null);
            return;
        }

        setIsMerging(true);

        try {
            if (user.uid) {
                await mergeVoxels(user.uid, voxelType, rarity);
            } else {
                throw new Error("User ID is missing for merging voxels.");
            }

            alert(`Successfully merged 2 x ${rarity} ${voxelType} into 1 x ${rarityHierarchy[rarity]} ${getNextRarity(rarity)} ${voxelType}!`);

        } catch (error) {
            console.error("Merge failed:", error);
            alert("Merge failed. Please try again.");
        } finally {
            setIsMerging(false);
            setSelectedMergeCandidate(null);
        }
    };


    const handleClose = () => {
        setIsOpen(false);
        onClose();
    };

    if (!isOpen) return null;

    const isInventoryEmpty = !loadingInventory && (!voxelInventory || (
        Object.keys(voxelInventory?.available).every((rarityKey: string) =>
            Object.keys(voxelInventory?.available[rarityKey as RarityKey]).every((voxelTypeKey: string) =>
                (voxelInventory?.available[rarityKey as RarityKey][voxelTypeKey as VoxelTypeKey] ?? 0) === 0
            )
        ) &&
        Object.keys(voxelInventory?.used).every((rarityKey: string) =>
            Object.keys(voxelInventory?.used[rarityKey as RarityKey]).every((voxelTypeKey: string) =>
                (voxelInventory?.used[rarityKey as RarityKey][voxelTypeKey as VoxelTypeKey] ?? 0) === 0
            )
        )
    ));


    const renderInventorySection = (sectionName: string, inventoryData: RarityInventory | undefined, currentActiveTab: 'available' | 'used') => {
        if (!inventoryData || activeTab !== currentActiveTab) return null;

        const hasItemsInSection = Object.keys(inventoryData).some((rarityKey: string) =>
            Object.keys(inventoryData[rarityKey as RarityKey]).some((voxelTypeKey: string) =>
                (inventoryData[rarityKey as RarityKey][voxelTypeKey as VoxelTypeKey] ?? 0) > 0
            )
        );

        if (!hasItemsInSection) {
            return (
                <div className="inventory-empty-section">
                    No items in {sectionName} Inventory.
                </div>
            );
        }

        return (
            <div className="inventory-section">
                <div className="voxel-boxes-container">
                    {rarityOrder.map((rarity) => {
                        const voxels = inventoryData[rarity] ?? {};
                        const entries = Object.entries(voxels).filter(([, count]) => count > 0);

                        if (entries.length === 0) return null;

                        return (
                            <div key={rarity} className="rarity-section">
                                <h4 className={`rarity-title ${rarity}`}>{rarity.toUpperCase()}</h4>
                                <div className="voxel-items-row">
                                    {entries.map(([type, count]) => {
                                        const voxelType = type as VoxelTypeKey;
                                        const isMergeCandidate = currentActiveTab === 'available' && count >= 2 && rarity !== 'legendary';

                                        const isSelectedCandidate = isMergeCandidate && selectedMergeCandidate?.rarity === rarity && selectedMergeCandidate?.type === voxelType;

                                        return (
                                            <div
                                                key={`${currentActiveTab}-${rarity}-${voxelType}`}
                                                className={`voxel-box ${rarity} ${isMergeCandidate ? 'merge-candidate' : ''} ${isSelectedCandidate ? 'merge-selected' : ''}`}
                                                onClick={() => handleVoxelBoxClick(rarity, voxelType, currentActiveTab)}
                                                title={`Alpha ${voxelType.replace('_', ' ')} - Qty: ${count}`}
                                            >
                                                {/* {imageUrl && (
                                                  <img src={imageUrl} alt={voxelType.replace('_', ' ')} className="voxel-image" />
                                              )} */}
                                                <div
                                                    className="voxel-image"
                                                    style={{ backgroundColor: VOXEL_COLORS[voxelType], width: '100%', height: '100%' }}
                                                    title={`Alpha ${voxelType.replace('_', ' ')}`}
                                                ></div>
                                                {count > 0 && (
                                                    <span className="voxel-quantity-subscript">{count}</span>
                                                )}

                                                {isSelectedCandidate && (
                                                    <button
                                                        className="merge-type-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMergeVoxelType();
                                                        }}
                                                        disabled={isMerging || !user || count < 2}
                                                    >
                                                        {isMerging ? 'Merging...' : 'Merge 2'}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };


    return (
        <div className="inventory-overlay">
            <div className="inventory-panel">
                <button className="close-button" onClick={handleClose}>✖</button>

                <h2>🎒 Your Voxel Inventory</h2>

                <div className="inventory-tabs">
                    <button
                        className={`tab-button ${activeTab === 'available' ? 'active' : ''}`}
                        onClick={() => setActiveTab('available')}
                    >
                        Available
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'used' ? 'active' : ''}`}
                        onClick={() => setActiveTab('used')}
                    >
                        Used
                    </button>
                </div>

                <div className="inventory-scroll-content">
                    {loadingInventory ? (
                        <div>Loading inventory...</div>
                    ) : (
                        isInventoryEmpty ? (
                            <div className="inventory-empty-message">Nothing in your inventory yet.<br />Visit the Market!</div>
                        ) : (
                            <>
                                {renderInventorySection('Available', voxelInventory?.available, 'available')}
                                {renderInventorySection('Used', voxelInventory?.used, 'used')}
                            </>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default InventoryPanel;
